import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Mail, User, Info, CheckCircle2, Loader2 } from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';

export default function SupportForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'support_tickets'), {
        ...formData,
        status: 'new',
        createdAt: serverTimestamp(),
        source: 'landing_page',
        userId: auth.currentUser?.uid || null
      });
      
      setSubmitted(true);
      toast.success("Support ticket synthesized successfully.");
    } catch (error: any) {
      console.error("Error submitting support ticket:", error);
      toast.error("Bridge connection failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-950/50 border border-emerald-500/20 rounded-[32px] p-12 text-center space-y-6 backdrop-blur-xl"
      >
        <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto text-emerald-500 border border-emerald-500/20">
          <CheckCircle2 size={40} />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-mono uppercase tracking-tighter text-white font-bold">Ticket Logged</h3>
          <p className="text-zinc-500 font-light max-w-sm mx-auto">
            Your inquiry has been synthesized into our support queue. Our crew will review and respond via email shortly.
          </p>
        </div>
        <button 
          onClick={() => setSubmitted(false)}
          className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
        >
          Submit Another Request
        </button>
      </motion.div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-8">
          <div className="tech-label">Crew Support</div>
          <h2 className="text-5xl font-mono uppercase tracking-tighter leading-[0.9] text-white">
            Access the <br/>
            <span className="text-[#0074D9]">Command Center.</span>
          </h2>
          <p className="text-zinc-500 text-lg font-light leading-relaxed">
            Need technical assistance, higher synthesis limits, or enterprise-grade deployment consulting? Our crew is standing by.
          </p>
          
          <div className="space-y-6 pt-4">
            <div className="flex items-center gap-4 text-zinc-400 group cursor-default">
              <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-center group-hover:border-[#0074D9]/50 transition-colors">
                <Mail size={18} />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest">support@yachtlabs.com</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-400 group cursor-default">
              <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-center group-hover:border-[#0074D9]/50 transition-colors">
                <MessageSquare size={18} />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest">Community Discord</span>
            </div>
          </div>
        </div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="bg-[#0A0A0A] border border-white/5 rounded-[32px] p-8 md:p-10 shadow-2xl backdrop-blur-xl relative"
        >
          {/* Subtle glow */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#0074D9]/10 rounded-full blur-[80px] pointer-events-none" />
          
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-1">Identity</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-700" size={14} />
                  <input 
                    type="text" 
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Commander Name"
                    className="w-full bg-black border border-white/5 rounded-xl py-4 pl-12 pr-6 text-sm focus:outline-none focus:border-[#0074D9] transition-all text-white placeholder:text-zinc-800"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-1">Return Path</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-700" size={14} />
                  <input 
                    type="email" 
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@base.com"
                    className="w-full bg-black border border-white/5 rounded-xl py-4 pl-12 pr-6 text-sm focus:outline-none focus:border-[#0074D9] transition-all text-white placeholder:text-zinc-800"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-1">Priority Subject</label>
              <div className="relative">
                <Info className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-700" size={14} />
                <select 
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full bg-black border border-white/5 rounded-xl py-4 pl-12 pr-6 text-sm focus:outline-none focus:border-[#0074D9] transition-all text-white appearance-none cursor-pointer"
                >
                  <option>General Inquiry</option>
                  <option>Technical Support</option>
                  <option>Synthesis Issues</option>
                  <option>Enterprise Licensing</option>
                  <option>Billing & Access</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-1">Message Payload</label>
              <textarea 
                required
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder="Transmitting creative intent or technical blockers..."
                rows={4}
                className="w-full bg-black border border-white/5 rounded-xl p-6 text-sm focus:outline-none focus:border-[#0074D9] transition-all text-white placeholder:text-zinc-800 resize-none"
              />
            </div>

            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-white text-black font-mono uppercase tracking-widest text-xs font-bold rounded-xl hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 group disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Transmitting...
                </>
              ) : (
                <>
                  Transmit Request
                  <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
